(() => {
  if (customElements.get('ombre-watch-viewer')) return;
  let rendererPromise;
  function loadRenderer(url) {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!rendererPromise) rendererPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.fetchPriority = 'low';
      script.src = url;
      script.onload = () => customElements.whenDefined('model-viewer').then(resolve);
      script.onerror = () => { script.remove(); rendererPromise = null; reject(new Error('3D renderer unavailable')); };
      document.head.append(script);
    });
    return rendererPromise;
  }
  customElements.define('ombre-watch-viewer', class extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.activateButton = this.querySelector('.on3d__activate');
        this.status = this.querySelector('.on3d__status');
        this.zoomIn = this.querySelector('[data-zoom="in"]');
        this.zoomOut = this.querySelector('[data-zoom="out"]');
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.activateButton.addEventListener('click', () => this.activate());
        this.zoomIn.addEventListener('click', () => this.zoom(.85));
        this.zoomOut.addEventListener('click', () => this.zoom(1.18));
      }
      this.connectionId = (this.connectionId || 0) + 1;
      this.activateButton.hidden = true;
      this.pageReady = document.readyState !== 'loading';
      this.near = this.dataset.loading === 'eager';
      this.onPageLoad = () => { this.pageReady = true; this.schedule(); };
      if (!this.pageReady) document.addEventListener('DOMContentLoaded', this.onPageLoad, {once: true});
      if (this.dataset.loading === 'eager') {
        this.schedule();
      } else if (typeof IntersectionObserver !== 'undefined') {
        this.observer = new IntersectionObserver(entries => {
          this.near = entries.some(entry => entry.isIntersecting);
          this.schedule();
        }, {rootMargin: this.dataset.loadMargin || '400px 0px'});
        this.observer.observe(this);
      } else {
        this.near = true;
        this.schedule();
      }
    }
    schedule() {
      if (!this.pageReady || !this.near || this.idleJob != null || this.dataset.state !== 'idle') return;
      // Steel prepares on page entry; the other watches retain their approach/idle gate.
      if (this.dataset.loading === 'eager') { this.activate(); return; }
      const connectionId = this.connectionId;
      const start = () => {
        this.idleJob = null;
        if (this.isConnected && this.near && this.connectionId === connectionId) this.activate();
      };
      this.idleJob = window.requestIdleCallback ? window.requestIdleCallback(start, {timeout: 700}) : setTimeout(start, 0);
    }
    disconnectedCallback() {
      this.connectionId++;
      this.observer?.disconnect();
      document.removeEventListener('DOMContentLoaded', this.onPageLoad);
      this.preloads?.forEach(link => link.remove());
      if (window.cancelIdleCallback) window.cancelIdleCallback(this.idleJob);
      else clearTimeout(this.idleJob);
      this.idleJob = null;
      clearTimeout(this.timer);
      this.stopClock();
      this.viewer?.remove();
      this.viewer = null;
      this.dataset.state = 'idle';
      this.setAttribute('aria-busy', 'false');
      this.activateButton.hidden = true;
      this.zoomIn.disabled = this.zoomOut.disabled = true;
      this.status.textContent = this.dataset.loading === 'eager' ? 'Preparing 3D view…' : '3D view loads automatically as you approach.';
    }
    async activate() {
      if (this.dataset.state === 'loading' || this.dataset.state === 'ready') return;
      const connectionId = this.connectionId;
      this.observer?.disconnect();
      this.dataset.state = 'loading';
      this.attempt = (this.attempt || 0) + 1;
      this.setAttribute('aria-busy', 'true');
      this.activateButton.disabled = true;
      this.activateButton.textContent = 'Loading 3D view…';
      this.status.textContent = 'Loading 3D view…';
      this.timer = setTimeout(() => this.fail(), 45000);
      try {
        // Failed loads are cached by model-viewer; an explicit retry needs a fresh cache key.
        const modelURL = new URL(this.dataset.model, location.href);
        const environmentURL = this.dataset.environment ? new URL(this.dataset.environment, location.href) : null;
        const decoderURL = new URL(this.dataset.decoder, location.href);
        // Keep HDR detection valid when Shopify adds its CDN version query.
        if (environmentURL) environmentURL.hash = 'studio.hdr';
        if (this.attempt > 1) {
          modelURL.searchParams.set('on3d_retry', String(this.attempt));
          environmentURL?.searchParams.set('on3d_retry', String(this.attempt));
          decoderURL.searchParams.set('on3d_retry', String(this.attempt));
        }
        // Download the unchanged model and studio lighting while the renderer loads/parses.
        // Steel also has early HTML hints; identical URLs let the browser reuse that download.
        this.preloads?.forEach(link => link.remove());
        this.preloads = [modelURL, environmentURL].filter(Boolean).map(url => {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'fetch';
          link.crossOrigin = 'anonymous';
          if (this.dataset.loading === 'eager') link.fetchPriority = 'low';
          link.href = url.href;
          document.head.append(link);
          return link;
        });
        await loadRenderer(this.dataset.renderer);
        if (!this.isConnected || this.connectionId !== connectionId || this.dataset.state !== 'loading') return;
        const viewer = document.createElement('model-viewer');
        this.viewer = viewer;
        viewer.ombreCooperative = Boolean(window.OmbreWatchPerformance);
        customElements.get('model-viewer').meshoptDecoderLocation = decoderURL.href;
        if (this.dataset.transcoder) customElements.get('model-viewer').ktx2TranscoderLocation = new URL('.', new URL(this.dataset.transcoder, location.href)).href;
        if (this.dataset.staticModel === 'true') customElements.get('model-viewer').minimumRenderScale = 1;
        const attributes = {
          src: modelURL.href, ...(environmentURL ? {'environment-image': environmentURL.href} : {}),
          alt: `${this.dataset.label || 'Melting Watch'}. Drag or use arrow keys to rotate; pinch or scroll to zoom.`,
          'camera-controls': '', 'touch-action': 'none', 'disable-pan': '', 'disable-tap': '',
          'camera-orbit': this.dataset.staticModel === 'true' ? '14deg 87deg 0.16m' : '14deg 87deg 0.18m', 'camera-target': 'auto auto auto',
          'min-camera-orbit': this.dataset.detailZoom === 'true' ? 'auto 0deg 0.065m' : 'auto 0deg 0.105m', 'max-camera-orbit': 'auto 180deg 0.24m',
          'field-of-view': '30deg', 'min-field-of-view': '30deg', 'max-field-of-view': '30deg',
          'tone-mapping': this.dataset.toneMapping || 'neutral', exposure: this.dataset.exposure || (this.dataset.staticModel === 'true' ? '1.2' : '1.8'), 'shadow-intensity': this.dataset.shadowIntensity || '0.3', 'shadow-softness': '1',
          'interaction-prompt': 'none', 'animation-name': 'EasternTime', 'animation-crossfade-duration': '0',
          loading: 'eager', tabindex: '0', 'aria-describedby': this.status.id
        };
        Object.entries(attributes).forEach(([key, value]) => viewer.setAttribute(key, value));
        // Settle the starting camera behind the poster; animate only shopper interactions.
        viewer.interpolationDecay = 0;
        let modelLoaded = false, frameReady = !viewer.ombreCooperative;
        const reveal = () => {
          if (!modelLoaded || !frameReady || this.viewer !== viewer || !this.isConnected || this.dataset.state !== 'loading') return;
          clearTimeout(this.timer);
          viewer.interpolationDecay = this.motion.matches ? 0 : 80;
          this.dataset.state = 'ready';
          this.setAttribute('aria-busy', 'false');
          this.activateButton.hidden = true;
          if (this.dataset.staticModel !== 'true') {
            if (!this.startClock()) { this.fail(); return; }
            this.status.textContent = 'Live Eastern Time · Pinch or scroll to zoom.';
          } else {
            this.status.textContent = 'Drag to rotate · Pinch or scroll to zoom.';
          }
          this.updateZoom();
        };
        viewer.addEventListener('ombre-first-render', () => { frameReady = true; reveal(); }, {once: true});
        viewer.addEventListener('load', () => {
          viewer.jumpCameraToGoal();
          modelLoaded = true;
          reveal();
        }, {once: true});
        viewer.addEventListener('error', () => { if (this.viewer === viewer) this.fail(); }, {once: true});
        viewer.addEventListener('camera-change', () => this.updateZoom());
        this.querySelector('.on3d__stage').append(viewer);
      } catch { if (this.isConnected && this.connectionId === connectionId) this.fail(); }
    }
    fail() {
      clearTimeout(this.timer);
      this.stopClock();
      this.viewer?.remove();
      this.viewer = null;
      this.dataset.state = 'error';
      this.setAttribute('aria-busy', 'false');
      this.activateButton.hidden = false;
      this.activateButton.disabled = false;
      this.activateButton.textContent = 'Try 3D again';
      this.status.textContent = '3D is unavailable. Product photos are still available.';
      this.zoomIn.disabled = this.zoomOut.disabled = true;
    }
    startClock() {
      if (!this.viewer.availableAnimations.includes('EasternTime')) return false;
      this.stopClock();
      this.viewer.pause();
      this.clockFormatter ||= new Intl.DateTimeFormat('en-GB', {
        timeZone: 'America/Toronto', hourCycle: 'h23', hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      this.syncClock();
      this.onClockVisibility = () => this.refreshClock();
      document.addEventListener('visibilitychange', this.onClockVisibility);
      window.addEventListener('pageshow', this.onClockVisibility);
      if (typeof IntersectionObserver !== 'undefined') {
        this.clockObserver = new IntersectionObserver(entries => {
          this.clockVisible = entries.some(entry => entry.isIntersecting);
          this.refreshClock();
        });
        this.clockObserver.observe(this);
      } else {
        this.clockVisible = true;
        this.refreshClock();
      }
      return true;
    }
    syncClock() {
      const parts = Object.fromEntries(this.clockFormatter.formatToParts(Date.now()).map(part => [part.type, part.value]));
      // The GLB's 12-hour clip contains independent, correctly pivoted hour/minute rotations.
      this.viewer.currentTime = (Number(parts.hour) % 12) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
    }
    refreshClock() {
      clearTimeout(this.clockTimer);
      if (!this.viewer || this.dataset.state !== 'ready' || !this.clockVisible || document.hidden) return;
      this.syncClock();
      this.clockTimer = setTimeout(() => this.refreshClock(), 1000 - Date.now() % 1000);
    }
    stopClock() {
      clearTimeout(this.clockTimer);
      this.clockObserver?.disconnect();
      if (this.onClockVisibility) {
        document.removeEventListener('visibilitychange', this.onClockVisibility);
        window.removeEventListener('pageshow', this.onClockVisibility);
      }
      this.clockVisible = false;
    }
    updateZoom() {
      if (!this.viewer || this.dataset.state !== 'ready') return;
      const orbit = this.viewer.getCameraOrbit();
      this.zoomIn.disabled = orbit.radius <= (this.dataset.detailZoom === 'true' ? .066 : .106);
      this.zoomOut.disabled = orbit.radius >= .239;
    }
    zoom(factor) {
      if (!this.viewer || this.dataset.state !== 'ready') return;
      const orbit = this.viewer.getCameraOrbit();
      const radius = Math.max(this.dataset.detailZoom === 'true' ? .065 : .105, Math.min(.24, orbit.radius * factor));
      this.viewer.cameraOrbit = `${orbit.theta}rad ${orbit.phi}rad ${radius}m`;
      this.viewer.interpolationDecay = this.motion.matches ? 0 : 80;
      if (this.motion.matches) this.viewer.jumpCameraToGoal();
    }
  });
})();
